/*
 * DSA_TOOLS — the declarative "tool-calling" surface of the DSA right-side
 * chat. Each entry is a named tool with a parameter shape, a human description,
 * and a buildPrompt() that turns a structured intent into an instruction for
 * the /chat LLM (the current transport). This mirrors an OpenAI-style function
 * schema so it can be promoted to real server-side tool-calling later without
 * changing the UI. The full contract is documented in docs/DSA_CHAT.md.
 *
 * Fields:
 *   key         — stable tool id (function name)
 *   label       — button text
 *   chip        — show as a quick-action chip in the chat header
 *   params      — JSON-schema-ish parameter description (for docs / future API)
 *   description — what the tool does (tooltip + docs)
 *   buildPrompt(args, ctx) — args = tool params; ctx = { problem, activeCase }
 */
export const DSA_TOOLS = [
  {
    key: 'get_hint',
    label: 'Hint',
    chip: true,
    params: { level: "enum('small','medium','strong') — how revealing (default 'small')" },
    description: 'A progressive hint that nudges toward the next step without revealing the full solution.',
    buildPrompt: (args = {}) => {
      const level = args.level || 'small';
      return `Give me a ${level} hint to move forward, one step at a time. Do NOT reveal the full solution unless I ask.`;
    },
  },
  {
    key: 'explain_code',
    label: 'Explain Code',
    chip: true,
    params: {},
    description: 'Explains what the current editor code does, line by line.',
    buildPrompt: () => 'Explain what my current code does, line by line, in plain language.',
  },
  {
    key: 'analyze_complexity',
    label: 'Complexity',
    chip: true,
    params: {},
    description: 'States the time and space complexity of the current code with justification.',
    buildPrompt: () => 'Analyze the time and space complexity of my current code. State Big-O for both and briefly justify each.',
  },
  {
    key: 'optimize_solution',
    label: 'Optimize',
    chip: true,
    params: {},
    description: 'Suggests a more efficient approach or micro-optimizations for the current code.',
    buildPrompt: () => 'Can my solution be more efficient? Suggest a better approach or optimizations, with the resulting complexity. Keep the explanation tight.',
  },
  {
    key: 'debug_code',
    label: 'Debug',
    chip: true,
    params: {},
    description: 'Reviews the current code for bugs and edge cases it may fail on.',
    buildPrompt: () => 'Review my current code for bugs or edge cases it might fail on (empty input, single element, all-same, all-unique). Point out each issue and how to fix it.',
  },
  {
    key: 'suggest_approach',
    label: 'Walkthrough',
    chip: true,
    params: { strategy: "optional string — a specific technique to frame around (e.g. 'sliding window')" },
    description: 'Walks through the optimal approach step by step.',
    buildPrompt: (args = {}) =>
      args.strategy
        ? `Walk me through an optimal approach using ${args.strategy}, step by step, before any code.`
        : 'Walk me through the optimal approach to this problem step by step, before writing any code.',
  },
  {
    key: 'run_tests',
    label: 'Run',
    chip: false,
    params: { case: 'integer — index of the test case to trace (default: active case)' },
    description: 'Traces the current code on a chosen test case and reports the actual vs. expected output.',
    buildPrompt: (args = {}, ctx = {}) => {
      const idx = Number.isInteger(args.case) ? args.case : (ctx.activeCase || 0);
      const tc = (ctx.problem?.testcases || [])[idx];
      if (!tc) return 'Trace my code on the sample test cases and report the output for each.';
      return `Trace my code on this test case: ${tc.input} (expected: ${tc.expected}). Show the step-by-step state, the actual output, and whether it PASSES or FAILS. Keep it brief.`;
    },
  },
  {
    key: 'evaluate_submission',
    label: 'Submit',
    chip: false,
    params: {},
    description: 'Evaluates the solution against all constraints and typical edge cases; gives a correctness/optimality verdict.',
    buildPrompt: () => 'Evaluate my solution against the constraints and all typical edge cases. Is it correct and optimal? Give a short PASS/FAIL-style verdict with reasons.',
  },
  {
    key: 'dry_run',
    label: 'Dry Run',
    chip: false,
    params: { input: 'string — a custom input to trace the code against' },
    description: 'Step-by-step trace of the current code on a custom input.',
    buildPrompt: (args = {}) => `Dry-run my current code on this input: ${args.input}. Show the variable state at each step and the final output.`,
  },
  {
    key: 'similar_problems',
    label: 'Similar',
    chip: true,
    params: {},
    description: 'Suggests related practice problems that reinforce the same pattern.',
    buildPrompt: () => 'Suggest 3–5 related problems that use the same core pattern as this one, with a one-line reason for each.',
  },
];

export default DSA_TOOLS;
